import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { API_URL } from '../../utils/apiUrl';
import { useTheme } from '../../contexts/ThemeContext';

const TermsPage = () => {
  const { t, i18n } = useTranslation('auth');
  const { theme, getGradient } = useTheme();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setLoading(true);
        const lang = i18n.language?.startsWith('hr') ? 'hr' : 'en';
        const response = await fetch(`${API_URL}/public/terms?lang=${lang}`);
        const data = await response.json();
        setContent(data.content || getDefaultTerms(lang));
      } catch (error) {
        console.error('Failed to fetch terms:', error);
        setContent(getDefaultTerms(i18n.language?.startsWith('hr') ? 'hr' : 'en'));
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, [i18n.language]);

  const getDefaultTerms = (lang) => {
    if (lang === 'hr') {
      return `UVJETI KORIŠTENJA

Posljednje ažurirano: ${new Date().toLocaleDateString('hr-HR')}

1. PRIHVAĆANJE UVJETA
Korištenjem ove aplikacije prihvaćate ove Uvjete korištenja. Ako se ne slažete s ovim uvjetima, molimo vas da ne koristite aplikaciju.

2. OPIS USLUGE
Ova aplikacija pruža platformu za upravljanje fitness treninzima, praćenje napretka i komunikaciju s vašim trenerom.

3. KORISNIČKI RAČUNI
- Odgovorni ste za održavanje povjerljivosti svojih podataka za prijavu
- Pristajete pružiti točne i potpune informacije
- Morate imati najmanje 18 godina za korištenje ove usluge

4. PRIHVATLJIVO KORIŠTENJE
Pristajete ne:
- Koristiti uslugu za bilo kakve nezakonite svrhe
- Dijeliti svoj račun s drugima
- Pokušavati dobiti neovlašteni pristup sustavu

5. ODRICANJE ODGOVORNOSTI
Usluga se pruža "kakva jest" bez ikakvih jamstava. Nismo odgovorni za bilo kakve ozljede nastale tijekom vježbanja.

6. IZMJENE UVJETA
Zadržavamo pravo izmjene ovih uvjeta u bilo kojem trenutku. Nastavak korištenja usluge predstavlja prihvaćanje novih uvjeta.

Za pitanja kontaktirajte svog trenera ili administratora.`;
    }
    
    return `TERMS OF SERVICE

Last updated: ${new Date().toLocaleDateString('en-US')}

1. ACCEPTANCE OF TERMS
By using this application, you agree to these Terms of Service. If you do not agree with these terms, please do not use the application.

2. DESCRIPTION OF SERVICE
This application provides a platform for managing fitness training, tracking progress, and communication with your trainer.

3. USER ACCOUNTS
- You are responsible for maintaining the confidentiality of your login credentials
- You agree to provide accurate and complete information
- You must be at least 18 years old to use this service

4. ACCEPTABLE USE
You agree not to:
- Use the service for any unlawful purposes
- Share your account with others
- Attempt to gain unauthorized access to the system

5. DISCLAIMER
The service is provided "as is" without any warranties. We are not responsible for any injuries sustained during exercise.

6. CHANGES TO TERMS
We reserve the right to modify these terms at any time. Continued use of the service constitutes acceptance of new terms.

For questions, contact your trainer or administrator.`;
  };

  const formatContent = (text) => {
    if (!text) return null;
    
    return text.split('\n').map((line, index) => {
      // Headers (all caps lines)
      if (line === line.toUpperCase() && line.trim().length > 0 && !line.match(/^\d+\./)) {
        return (
          <h2 key={index} className="text-xl font-bold text-gray-900 mt-6 mb-3">
            {line}
          </h2>
        );
      }
      // Numbered sections
      if (line.match(/^\d+\.\s+[A-Z]/)) {
        return (
          <h3 key={index} className="text-lg font-semibold text-gray-800 mt-4 mb-2">
            {line}
          </h3>
        );
      }
      // Bullet points
      if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
        return (
          <li key={index} className="ml-6 text-gray-700 mb-1">
            {line.replace(/^[-•]\s*/, '')}
          </li>
        );
      }
      // Empty lines
      if (line.trim() === '') {
        return <div key={index} className="h-2" />;
      }
      // Regular paragraphs
      return (
        <p key={index} className="text-gray-700 mb-2">
          {line}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Helmet>
        <title>Uvjeti Korištenja | ProTrener</title>
        <meta name="description" content="Uvjeti korištenja ProTrener aplikacije. Pročitajte pravila i odredbe prije korištenja usluge." />
        <link rel="canonical" href="https://protrener.app/terms" />
        <meta property="og:title" content="Uvjeti Korištenja | ProTrener" />
        <meta property="og:description" content="Uvjeti korištenja ProTrener aplikacije." />
        <meta property="og:image" content="https://protrener.app/favicon.ico" />
        <meta property="og:url" content="https://protrener.app/terms" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="hr_HR" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Uvjeti Korištenja | ProTrener" />
        <meta name="twitter:description" content="Uvjeti korištenja ProTrener aplikacije." />
      </Helmet>
      {/* Header */}
      <div className={`bg-gradient-to-r ${getGradient(0)} py-6 px-4 native-safe-top`}>
        <div className="max-w-3xl mx-auto">
          <Link
            to="/login"
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t('legal.backToLogin', 'Back to Login')}
          </Link>
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-white mr-3" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              {t('legal.termsOfService', 'Terms of Service')}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="prose prose-gray max-w-none">
              {formatContent(content)}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <Link
            to="/privacy"
            className={`${theme.sectionLink} font-semibold`}
          >
            {t('legal.viewPrivacyPolicy', 'View Privacy Policy')} →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
